import { RouteName, TaftKeycloakRoles } from '@/utils/constants';
import AccountConfirmationView from '@/views/account/AccountConfirmationView.vue';
import AccountCreateSuccessView from '@/views/account/AccountCreateSuccessView.vue';
import AccountCreateView from '@/views/account/AccountCreateView.vue';
import BaseRegistration from '@/components/base-registration/BaseRegistration.vue';
import HomeView from '@/views/HomeView.vue';
import Logout from '@/views/Logout.vue';
import MainView from '@/views/MainView.vue';
import MainViewMinistry from '@/views/MainViewMinistry.vue';
import NotFound404 from '@/views/NotFound404.vue';
import OidcCallbackErrorView from '@/views/auth/OidcCallbackErrorView.vue';
import OidcCallbackView from '@/views/auth/OidcCallbackView.vue';
import PrivacyPolicyView from '@/views/PrivacyPolicyView.vue';
import { RouteConfig } from 'vue-router';
import UnauthorizedView from '@/views/UnauthorizedView.vue';
import VerifyEmailView from '@/views/auth/VerifyEmailView.vue';

class RoleGroup {
  static retailerSuperUser: string[] = [
    TaftKeycloakRoles.TAFT_RETAILER_WEBAPP_POS,
    TaftKeycloakRoles.TAFT_RETAILER_WEBAPP_REPORTS,
  ];
  static ministrySuperUser: string[] = [
    TaftKeycloakRoles.TAFT_MINISTRY_REPORTS,
    TaftKeycloakRoles.TAFT_MINISTRY_TASKS,
  ];
}

export default function getRoutes(): RouteConfig[] {
  const routes: Array<RouteConfig> = [
    {
      path: '/',
      component: BaseRegistration,
      children: [
        {
          path: '',
          name: 'home',
          component: HomeView,
          meta: {
            isPublic: true,
          },
        },
        {
          path: '/create-account',
          name: 'CreateAccount',
          component: AccountCreateView,
          meta: {
            isPublic: true,
          },
        },
        {
          path: '/confirm-account-details',
          name: 'ConfirmAccountDetails',
          component: AccountConfirmationView,
          meta: {
            isPublic: true,
          },
        },
      ],
      meta: {
        isPublic: true,
      },
    },
    {
      path: '/create-account-success',
      name: 'CreateAccountSuccess',
      component: AccountCreateSuccessView,
      meta: {
        isPublic: true,
      },
    },
    {
      path: '/privacy-policy',
      name: 'PrivacyPolicy',
      component: PrivacyPolicyView,
      meta: {
        isPublic: true,
      },
    },
    {
      path: `/${RouteName.Main}/`,
      component: MainView,
      meta: {
        roles: RoleGroup.retailerSuperUser,
      },
      children: [
        {
          path: RouteName.Dashboard,
          name: 'Dashboard',
          // route level code-splitting
          // this generates a separate chunk (about.[hash].js) for this route
          // which is lazy-loaded when the route is visited.
          component: () => import(/* webpackChunkName: "dashboard" */ '@/views/dashboard/DashboardView.vue'),
          meta: {
            roles: [TaftKeycloakRoles.TAFT_RETAILER_WEBAPP_POS],
          },
        },
        {
          path: 'summary',
          name: 'Transaction_Summary',
          // route level code-splitting
          // this generates a separate chunk (about.[hash].js) for this route
          // which is lazy-loaded when the route is visited.
          component: () => import(/* webpackChunkName: "summary" */ '@/views/dashboard/TransactionSummaryView.vue'),
          meta: {
            roles: [TaftKeycloakRoles.TAFT_RETAILER_WEBAPP_POS],
          },
        },
        {
          path: 'settings/',
          name: 'Settings',
          redirect: 'settings/manage-products',
          component: () => import(/* webpackChunkName: "settings" */ '@/views/settings/SettingsView.vue'),
          meta: {
            roles: [TaftKeycloakRoles.TAFT_RETAILER_WEBAPP_POS],
          },
          children: [
            {
              path: 'manage-users',
              name: 'Manage users',
              component: () => import(/* webpackChunkName: "users" */ '@/views/settings/users/ManageUsersView.vue'),
              meta: {
                roles: [TaftKeycloakRoles.TAFT_RETAILER_WEBAPP_POS],
              },
            },
            {
              path: 'manage-devices',
              name: 'Manage devices',
              component: () => import(/* webpackChunkName: "devices" */ '@/views/settings/devices/ManageDevicesView.vue'),
              meta: {
                roles: [TaftKeycloakRoles.TAFT_RETAILER_WEBAPP_POS],
              },
            },
            {
              path: 'manage-products',
              name: 'Manage products',
              component: () => import(/* webpackChunkName: "products" */ '@/views/settings/products/ManageProductsView.vue'),
              meta: {
                roles: [TaftKeycloakRoles.TAFT_RETAILER_WEBAPP_POS],
              },
            },
          ],
        },
        {
          path: 'reports/',
          name: 'Reports',
          component: () => import(/* webpackChunkName: "reports" */ '@/views/reports/ReportsView.vue'),
          redirect: 'reports/transaction-history',
          children: [
            {
              path: 'transaction-history',
              name: 'Transaction History',
              component: () => import(/* webpackChunkName: "transaction_history" */ '@/views/reports/transaction_history/TransactionHistoryView.vue'),
              meta: {
                roles: RoleGroup.retailerSuperUser,
              },
            },
            {
              path: 'monthly-sales',
              name: 'Monthly Sales',
              component: () => import(/* webpackChunkName: "monthly_sales" */ '@/views/reports/monthly_sales/MonthlySalesView.vue'),
              meta: {
                roles: RoleGroup.retailerSuperUser,
              },
            },
            {
              path: 'retailer-band-sales-history',
              name: 'Retailer Band Sales History',
              component: () => import(/* webpackChunkName: "band_sales_history" */ '@/views/reports/band_sales_history/BandSalesHistoryView.vue'),
              meta: {
                roles: RoleGroup.retailerSuperUser,
              },
            },
            {
              path: 'filing-summary',
              name: 'Filing Summary',
              component: () => import(/* webpackChunkName: "transaction_history" */ '@/views/reports/filing_summary/FilingSummaryView.vue'),
              meta: {
                roles: RoleGroup.retailerSuperUser,
              },
            },
          ],
        },
        {
          path: 'support',
          name: 'Support',
          component: () => import(/* webpackChunkName: "support" */ '@/views/support/SupportView.vue'),
          meta: {
            roles: [TaftKeycloakRoles.TAFT_RETAILER_WEBAPP_POS],
          },
        },
        {
          path: 'guides/',
          name: 'Guides',
          component: () => import(/* webpackChunkName: "guides" */ '@/views/guides/GuidesView.vue'),
          children: [
            {
              path: 'web-docs',
              name: 'Web App Docs',
              component: () => import(/* webpackChunkName: "web_docs" */ '@/views/guides/web-docs/WebDocsView.vue'),
              meta: {
                roles: [TaftKeycloakRoles.TAFT_RETAILER_WEBAPP_POS],
              },
            },
            {
              path: 'ipad-docs',
              name: 'iPad POS App Docs',
              component: () => import(/* webpackChunkName: "pos_docs" */ '@/views/guides/pos-docs/PosDocsView.vue'),
              meta: {
                roles: [TaftKeycloakRoles.TAFT_RETAILER_WEBAPP_POS],
              },
            },
            {
              path: 'etax-instructions',
              name: 'eTaxBC Filing Instructions',
              component: () => import(/* webpackChunkName: "etax_instructions" */ '@/views/guides/etax-instructions/ETaxInstructionsView.vue'),
              meta: {
                roles: [TaftKeycloakRoles.TAFT_RETAILER_WEBAPP_POS],
              },
            },
          ],
        },
      ],
    },
    {
      path: `/${RouteName.Ministry}/`,
      component: MainViewMinistry,
      meta: {
        roles: RoleGroup.ministrySuperUser,
      },
      children: [
        {
          path: `${RouteName.Reports}/`,
          component: () => import(/* webpackChunkName: "ministry_reports" */ '@/views/ministry/MinistryReportsView.vue'),
          meta: {
            roles: [
              TaftKeycloakRoles.TAFT_MINISTRY_REPORTS,
              TaftKeycloakRoles.TAFT_MINISTRY_CITIZEN_REPORTS,
            ],
          },
          children: [
            {
              path: '',
              name: 'Ministry Reports Dashboard',
              component: () => import(/* webpackChunkName: "retailer_sales_dashboard" */ '@/views/ministry/reports/ReportsDashboardView.vue'),
              meta: {
                roles: [
                  TaftKeycloakRoles.TAFT_MINISTRY_REPORTS,
                  TaftKeycloakRoles.TAFT_MINISTRY_CITIZEN_REPORTS,
                ],
              },
            },
            {
              path: 'retailer-sales-history',

              component: () => import(/* webpackChunkName: "retailer_sales_history" */ '@/views/ministry/reports/retailer_sales_history/RetailerSalesHistoryView.vue'),
              meta: {
                roles: [TaftKeycloakRoles.TAFT_MINISTRY_REPORTS],
              },
            },
            {
              path: 'retailer-sales-summary',
              name: 'Retailer Sales Summary',
              component: () => import(/* webpackChunkName: "retailer_sales_summary" */ '@/views/ministry/reports/retailer_sales_summary/RetailerSalesSummaryView.vue'),
              meta: {
                roles: [TaftKeycloakRoles.TAFT_MINISTRY_REPORTS],
              },
            },
            {
              path: 'citizen-sales-history',
              name: 'Citizen Sales History',
              component: () => import(/* webpackChunkName: "citizen_sales_history" */ '@/views/ministry/reports/citizen_sales_summary/CitizenSalesSummaryView.vue'),
              meta: {
                roles: [
                  TaftKeycloakRoles.TAFT_MINISTRY_REPORTS,
                  TaftKeycloakRoles.TAFT_MINISTRY_CITIZEN_REPORTS,
                ],
              },
            },
            {
              path: 'band-sales-history',
              name: 'Band Sales History',
              component: () => import(/* webpackChunkName: "band_sales_history" */ '@/views/ministry/reports/band_sales_summary/BandSalesSummaryView.vue'),
              meta: {
                roles: [TaftKeycloakRoles.TAFT_MINISTRY_REPORTS],
              },
            },
            {
              path: 'denied-adjudications',
              name: 'Denied Adjudications',
              component: () => import(/* webpackChunkName: "denied_adjudications" */ '@/views/ministry/reports/denied_adjudications/DeniedAdjudicationsView.vue'),
              meta: {
                roles: [TaftKeycloakRoles.TAFT_MINISTRY_REPORTS],
              },
            },
            {
              path: 'high-volume-fuel-sales',
              name: 'High Volume Fuel Sales',
              component: () => import(/* webpackChunkName: "high_volume_fuel_sales" */ '@/views/ministry/reports/high_volume_fuel_sales/HighVolumeFuelSalesView.vue'),
              meta: {
                roles: [TaftKeycloakRoles.TAFT_MINISTRY_REPORTS],
              },
            },
            {
              path: 'multiple-fuel-sales-monthly',
              name: 'Multiple Fuel Sales Monthly',
              component: () => import(/* webpackChunkName: "multiple_fuel_sales_monthly" */ '@/views/ministry/reports/multiple_fuel_sales_monthly/MultipleFuelSalesMonthlyView.vue'),
              meta: {
                roles: [TaftKeycloakRoles.TAFT_MINISTRY_REPORTS],
              },
            },
          ],
        },
        {
          path: RouteName.TaskList,
          name: 'Task List',
          component: () => import(/* webpackChunkName: "task_list" */ '@/views/ministry/TaskListView.vue'),
          meta: {
            roles: [
              TaftKeycloakRoles.TAFT_MINISTRY_TASKS,
              TaftKeycloakRoles.TAFT_MINISTRY_TASKS_PENDING_STATUS_NUMBERS,
            ],
          },
          children: [
            {
              path: '',
              name: 'Tasks Dashboard',
              component: () => import(/* webpackChunkName: "retailer_sales_dashboard" */ '@/views/ministry/tasks/TasksDashboardView.vue'),
              meta: {
                roles: [
                  TaftKeycloakRoles.TAFT_MINISTRY_TASKS,
                  TaftKeycloakRoles.TAFT_MINISTRY_TASKS_PENDING_STATUS_NUMBERS,
                ],
              },
            },
            {
              path: 'pending-status-numbers',
              name: 'Pending Status Numbers',
              component: () => import(/* webpackChunkName: "retailer_sales_dashboard" */ '@/views/ministry/tasks/pending_status_numbers/PendingStatusNumbersView.vue'),
              meta: {
                roles: [
                  TaftKeycloakRoles.TAFT_MINISTRY_TASKS_PENDING_STATUS_NUMBERS,
                ],
              },
            },
          ],
        },
      ],
    },
    {
      path: '/logout',
      name: RouteName.Logout,
      component: Logout,
    },
    {
      path: '/oidc-callback', // Needs to match redirectUri (redirect_uri if you use snake case) in you oidcSettings
      name: 'oidcCallback',
      component: OidcCallbackView,
      meta: {
        isOidcCallback: true,
      },
    },
    {
      path: '/oidc-callback-error',
      name: 'oidcCallbackError',
      component: OidcCallbackErrorView,
      meta: {
        isPublic: true,
        isOidcCallback: true,
      },
    },
    {
      path: '/verify-email',
      name: RouteName.VerifyEmail,
      component: VerifyEmailView,
      meta: {
        isPublic: true,
      },
    },
    {
      path: '/unauthorized',
      name: 'Unauthorized',
      component: UnauthorizedView,
      meta: {
        isPublic: true,
      },
    },
    {
      path: '/:catchAll(.*)',
      name: 'notFound',
      component: NotFound404,
      meta: {
        isPublic: true,
      },
    },
  ];
  return routes;
}
